/**
 * SollDeckungTab — Ausschreibungs-Screen Tab 2 (P2 Placement Slice 5).
 *
 * The Soll-Deckungskatalog: the E12 Pflichtdeckung warn banner (with «Kenntnisnahme»), the Kunde
 * Basissparten hint strip (Q27), the «x von y Vorgaben beantwortet» progress, and one answer row
 * per resolved Police Soll rule (Zielsumme/Selbstbehalt/Prioritaet/Begruendung commit-on-blur,
 * Status select + «Bestaetigen», the «aus Police» + «kritisch» chips).
 *
 * [Source: apps/web/src/features/entity-detail/components/police-risiko/SollGapSection.tsx] (table idiom)
 */

import { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  SOLL_PFLICHT_STATUS_LABELS,
  STEP2_ANSWER_STATUS,
  STEP2_ANSWER_STATUS_LABELS,
  STEP2_ANSWER_STATUS_VALUES,
} from '@brokly/shared';
import type { Step2AnswerItem, Step2AnswerPutBody, Step2AnswerStatus } from '@brokly/shared';
import { Button } from '../../components/ui/Button';
import { PlacementHinweisbox } from './PlacementHinweisbox';
import { usePlacementSollDeckung, usePutAnswer, usePflichtdeckungKenntnisnahme } from './usePlacementSnapshot';

export function SollDeckungTab({ placementId, canWrite }: { placementId: string; canWrite: boolean }) {
  const query = usePlacementSollDeckung(placementId);
  const putAnswer = usePutAnswer(placementId);
  const kenntnis = usePflichtdeckungKenntnisnahme(placementId);

  if (query.isLoading) return <p className="p-4 text-sm text-muted-foreground">Lade Soll-Deckung…</p>;
  if (query.isError || !query.data) {
    return <p className="p-4 text-sm text-status-danger" data-testid="plt2-error">Soll-Deckung konnte nicht geladen werden.</p>;
  }

  const data = query.data;
  const editable = data.editable && canWrite;
  const pd = data.pflichtdeckung;

  const mutationError = putAnswer.isError || kenntnis.isError;

  return (
    <div className="flex flex-col gap-4 py-4" data-testid="placement-soll-deckung-tab">
      <PlacementHinweisbox />

      {mutationError && (
        <div className="rounded-lg border border-status-danger/40 bg-status-danger/5 p-3 text-sm text-status-danger" role="alert" data-testid="plt2-mutation-error">
          Speichern fehlgeschlagen. Bitte Eingaben pruefen und erneut versuchen.
        </div>
      )}

      {pd.warnflag && (
        <div className="flex items-start justify-between gap-3 rounded-lg border border-status-danger/40 bg-status-danger/5 p-3" data-testid="plt2-warn-banner">
          <div className="flex items-start gap-2 text-sm text-status-danger">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>
              Kritische Pflichtdeckung unbestaetigt: {pd.warn_rule_keys.join(', ')}.
            </span>
          </div>
          {canWrite && (
            <Button size="sm" variant="outline" onClick={() => kenntnis.mutate()} disabled={kenntnis.isPending} data-testid="plt2-kenntnisnahme">
              {kenntnis.isPending ? 'Speichern…' : 'Kenntnisnahme'}
            </Button>
          )}
        </div>
      )}

      {data.basissparten_hinweis.length > 0 && (
        <div className="rounded-lg border border-dashed bg-muted/20 p-3 text-xs text-muted-foreground" data-testid="plt2-basissparten-hinweis">
          <span className="font-medium">Basissparten-Hinweis:</span>{' '}
          {data.basissparten_hinweis.map((b) => b.label).join(', ')}
        </div>
      )}

      <p className="text-sm font-medium text-foreground" data-testid="plt2-progress">
        {data.progress.beantwortet} von {data.progress.total} Vorgaben beantwortet
      </p>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="border-b text-left text-xs uppercase text-muted-foreground">
              <th className="py-2 pr-2">Vorgabe</th>
              <th className="py-2 pr-2">Zielsumme</th>
              <th className="py-2 pr-2">Selbstbehalt</th>
              <th className="py-2 pr-2">Prioritaet</th>
              <th className="py-2 pr-2">Begruendung</th>
              <th className="py-2 pr-2">Status</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {data.items.map((item) => (
              <Step2AnswerRow
                key={item.rule_key}
                item={item}
                editable={editable}
                isSaving={putAnswer.isPending}
                onCommit={(body) => putAnswer.mutate({ ruleKey: item.rule_key, body })}
              />
            ))}
          </tbody>
        </table>
        {data.items.length === 0 && <p className="py-3 text-sm text-muted-foreground">Keine Soll-Vorgaben fuer diese Sparte.</p>}
      </div>
    </div>
  );
}

/** Empty string → null; a finite number → itself; anything else → undefined (invalid, skip). */
function numOrNull(s: string): number | null | undefined {
  const t = s.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : undefined;
}

/** Like numOrNull, but Prioritaet must be a whole number (non-integer → undefined = rejected). */
function intOrNull(s: string): number | null | undefined {
  const v = numOrNull(s);
  if (v === undefined || v === null) return v;
  return Number.isInteger(v) ? v : undefined;
}

function Step2AnswerRow({
  item,
  editable,
  isSaving,
  onCommit,
}: {
  item: Step2AnswerItem;
  editable: boolean;
  isSaving: boolean;
  onCommit: (body: Step2AnswerPutBody) => void;
}) {
  const [ziel, setZiel] = useState(item.target_value_override != null ? String(item.target_value_override) : '');
  const [selbst, setSelbst] = useState(item.selbstbehalt_override != null ? String(item.selbstbehalt_override) : '');
  const [prio, setPrio] = useState(item.prioritaet_override != null ? String(item.prioritaet_override) : '');
  const [begruendung, setBegruendung] = useState(item.freitext_begruendung ?? '');
  const [prioInvalid, setPrioInvalid] = useState(false);

  // Re-sync the row inputs whenever the server copy changes (after a refetch, a 409 rollback, or a
  // sibling edit) — useState only seeds the FIRST render, so without this a stale input would stick.
  useEffect(() => {
    setZiel(item.target_value_override != null ? String(item.target_value_override) : '');
    setSelbst(item.selbstbehalt_override != null ? String(item.selbstbehalt_override) : '');
    setPrio(item.prioritaet_override != null ? String(item.prioritaet_override) : '');
    setBegruendung(item.freitext_begruendung ?? '');
    setPrioInvalid(false);
  }, [item.target_value_override, item.selbstbehalt_override, item.prioritaet_override, item.freitext_begruendung]);

  const commitField = (patch: Step2AnswerPutBody) => {
    if (!editable) return;
    onCommit(patch);
  };

  return (
    <tr className="border-b align-top" data-testid={`plt2-row-${item.rule_key}`}>
      <td className="py-2 pr-2">
        <div className="flex flex-col gap-0.5">
          <span className="font-medium text-foreground">{item.label}</span>
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            {SOLL_PFLICHT_STATUS_LABELS[item.pflicht_status]}
            {item.is_kritisch && <span className="rounded bg-status-danger/10 px-1 text-status-danger" data-testid="plt2-kritisch">kritisch</span>}
            {item.prefill_source === 'police' && (
              <span className="rounded bg-muted px-1" data-testid="plt2-police">
                {item.prefill_hidden ? 'aus Police (verborgen)' : 'aus Police'}
              </span>
            )}
          </span>
        </div>
      </td>
      <td className="py-2 pr-2">
        <input
          value={ziel}
          onChange={(e) => setZiel(e.target.value)}
          onBlur={() => { const v = numOrNull(ziel); if (v !== undefined && v !== item.target_value_override) commitField({ target_value_override: v }); }}
          disabled={!editable || isSaving}
          aria-label={`Zielsumme ${item.rule_key}`}
          className="w-28 rounded-md border bg-card px-2 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
        />
      </td>
      <td className="py-2 pr-2">
        <input
          value={selbst}
          onChange={(e) => setSelbst(e.target.value)}
          onBlur={() => { const v = numOrNull(selbst); if (v !== undefined && v !== item.selbstbehalt_override) commitField({ selbstbehalt_override: v }); }}
          disabled={!editable || isSaving}
          aria-label={`Selbstbehalt ${item.rule_key}`}
          className="w-24 rounded-md border bg-card px-2 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
        />
      </td>
      <td className="py-2 pr-2">
        <input
          value={prio}
          onChange={(e) => { setPrio(e.target.value); setPrioInvalid(intOrNull(e.target.value) === undefined); }}
          onBlur={() => {
            const v = intOrNull(prio);
            if (v === undefined) { setPrioInvalid(true); return; } // non-integer → reject, do not commit
            setPrioInvalid(false);
            if (v !== item.prioritaet_override) commitField({ prioritaet_override: v });
          }}
          disabled={!editable || isSaving}
          aria-label={`Prioritaet ${item.rule_key}`}
          aria-invalid={prioInvalid}
          className={`w-16 rounded-md border bg-card px-2 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60 ${prioInvalid ? 'border-status-danger' : ''}`}
        />
      </td>
      <td className="py-2 pr-2">
        <input
          value={begruendung}
          onChange={(e) => setBegruendung(e.target.value)}
          onBlur={() => { const v = begruendung.trim() === '' ? null : begruendung; if (v !== item.freitext_begruendung) commitField({ freitext_begruendung: v }); }}
          disabled={!editable || isSaving}
          aria-label={`Begruendung ${item.rule_key}`}
          className="w-40 rounded-md border bg-card px-2 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
        />
      </td>
      <td className="py-2 pr-2">
        {editable ? (
          <select
            value={item.status}
            onChange={(e) => { const s = e.target.value as Step2AnswerStatus; if (s !== item.status) commitField({ status: s }); }}
            disabled={isSaving}
            aria-label={`Status ${item.rule_key}`}
            data-testid={`plt2-status-${item.rule_key}`}
            className="rounded-md border bg-card px-2 py-1 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
          >
            {STEP2_ANSWER_STATUS_VALUES.map((s) => (
              <option key={s} value={s}>{STEP2_ANSWER_STATUS_LABELS[s]}</option>
            ))}
          </select>
        ) : (
          <span className="text-xs" data-testid={`plt2-status-${item.rule_key}`}>{STEP2_ANSWER_STATUS_LABELS[item.status]}</span>
        )}
      </td>
      <td className="py-2">
        {editable && item.status !== STEP2_ANSWER_STATUS.BESTAETIGT && (
          <Button size="sm" variant="outline" disabled={isSaving} onClick={() => commitField({ status: STEP2_ANSWER_STATUS.BESTAETIGT })} data-testid={`plt2-confirm-${item.rule_key}`}>
            Bestaetigen
          </Button>
        )}
      </td>
    </tr>
  );
}
